/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import isFunction from 'lodash/isFunction';
import isNil from 'lodash/isNil';

/**
 * Konečný stavový automat.
 * Využívá plugin
 * [Javascript State Machine (JSM)](https://github.com/jakesgordon/javascript-state-machine/tree/7ae0a4e02e30a2627b357e8e44fbf0c6a871bcb7),
 * od kterého přebírá většinu rozhraní.
 */
class StateMachine {
  /**
   * Vytvoří instanci.
   * @param {Class.<JavascriptStateMachine>} JavascriptStateMachine
   * @param {Object} options Nastavení ve formátu JSM. Kromě položky `methods`.
   * @returns {StateMachine} Nová instance.
   */
  constructor(JavascriptStateMachine, options) {
    /**
     * Async operace, které se mají provést při přechodech.
     * @private
     * @type {?Promise}
     */
    this.pendingPromise = null;
    /**
     * Plugin realizující většinu funkcionality stavového automatu.
     * @private
     * @type {JavascriptStateMachine}
     */
    this.jsm = new JavascriptStateMachine({
      ...options,
      methods: {
        onBeforeTransition: (...argArray) =>
          this.onBeforeTransition(...argArray),
        onInvalidTransition: (...argArray) =>
          this.onInvalidTransition(...argArray),
        onPendingTransition: (...argArray) =>
          this.onPendingTransition(...argArray)
      }
    });
  }

  /**
   * Zjistí aktuální stav automatu.
   * @returns {string} Název stavu.
   */
  getState() {
    return this.jsm.state;
  }

  /**
   * Reakce na začátek přechodu.
   * @private
   * @returns {Promise|undefined} Požadovaná async operace.
   */
  onBeforeTransition() {
    return this.pendingPromise;
  }

  /**
   * Reakce na neplatný přechod.
   * @param {string} transition Název přechodu, který selhal.
   * @param {string} from Název stavu, na kterém byl požadován přechod.
   * @private
   * @returns {undefined} Nic.
   */
  onInvalidTransition(transition, from) {
    throw new Error(
      `Invalid transition "${transition}" from the state "${from}".`
    );
  }

  /**
   * Reakce na přechod, bez dokončení předchozího.
   * @param {string} transition Název přechodu, který selhal.
   * @param {string} from Název stavu, na kterém byl požadován přechod.
   * @private
   * @returns {undefined} Nic.
   */
  onPendingTransition(transition, from) {
    throw new Error(
      `Can not transition "${transition}" from the state "${from}". Another transition is pending.`
    );
  }

  /**
   * Vytvoří bezpečný synchronní přechod mezi stavy.
   * Kontroluje platnost přechodu před provedením operací.
   * Při selhání operací se přechod neprovede.
   * @param {string} transition Název přechodu.
   * @param {Function} cb Funkce, která se zavolá při přechodu.
   * @returns {*} Výsledek funkce.
   */
  transit(transition, cb) {
    // Kontrola vstupu.
    if (!isFunction(cb)) {
      throw TypeError(`Invalid callback ${cb}.`);
    }
    const { jsm } = this;
    // Kontrola, jestli lze přechod provést.
    if (jsm.cannot(transition)) {
      this.onInvalidTransition(transition, jsm.state);
    }
    // Provedení funkce.
    // Provádí se pouze jednou a se záměrem provést další operace po dokončení.
    // To splňuje požadavky ESLint a přesto selhává. Uvedeno jako "Known Limitations".
    // eslint-disable-next-line node/callback-return
    const result = cb();
    // Provedení přechodu.
    jsm[transition]();
    // Vrácení výsledku funkce.
    return result;
  }

  /**
   * Vytvoří bezpečný asynchronní přechod mezi stavy.
   * Během operací jsou blokovány ostatní požadavky na přechod.
   * Při selhání operací se přechod neprovede.
   * @param {string} transition Název přechodu.
   * @param {Function} cb Funkce, na kterou se čeká při přechodu.
   * @returns {Promise.<*>} Výsledek funkce.
   */
  transitAsync(transition, cb) {
    return new Promise((resolve) => {
      // Kontrola vstupu.
      if (!isFunction(cb)) {
        throw TypeError(`Invalid callback ${cb}.`);
      }
      // Kontrola, jestli neprobíhá jiný asynchronní přechod.
      const { jsm, pendingPromise } = this;
      if (!isNil(pendingPromise)) {
        this.onPendingTransition(transition, jsm.state);
      }
      // Kontrola, jestli lze přechod provést.
      if (jsm.cannot(transition)) {
        this.onInvalidTransition(transition, jsm.state);
      }
      // Získání Promise, na kterou se má čekat.
      const promise = this.wrapPromise(cb);
      // Uložení Promise pro předání do FSM přes "Lifecycle Events".
      // https://github.com/jakesgordon/javascript-state-machine/blob/ca1a2f380384d83f85ddc669c9798a9a96d6aca6/docs/async-transitions.md
      this.pendingPromise = promise;
      // Provedení přechodu a vrácení výsledku Promise.
      // Spoléhá se na to, že vrácení promise v `onBeforeTransition` způsobí vrácení té stejné promise při přechodu.
      // Přechod ve skutečnosti vrací jinou promise, která "obaluje" tu vloženou v `onBeforeTransition`.
      // Je nutné čekat na "obalující" promise, jinak dochází ke vzniku chyb `Uncaught (in promise)`.
      // Bohužel ale "obalující" promise nevrací výsledek vstupní promise, proto je nakonec vynceno jeho vrácení.
      // TODO Nešlo by aktivovat přechod univerzálněji? Může kolidovat s názvy jako `state`, `can`...
      // - Lze pomocí `jsm._fsm.fire(transition, [])`.
      // - Je otázkou, co to vyřeší. Muselo by se asi kompletně přejít na používání `_fsm`.
      // - Když by byl např. definován stav `cannot`, tak nelze používat `jsm.cannot(transition)`, ale `jsm._fsm.cannot(transition)`.
      // - Je otázkou, jestli kompletní přesun implementace na privátní rozhraní `_fsm` není horší přístup, než řešený problém.
      // - Možná reportovat do issues? Asi by vyžadovalo více testů, jak se v tomto případě bude FSM chovat.
      // - Možná zkusit vyřešit voláním `jsm.prototype.cannot`. Ale pravděpodobně jsou všechny tyto metody napojeny přímo na instanci.
      return resolve(
        this.jsm[transition]()
          .then(() => promise)
          .finally(() => {
            this.pendingPromise = null;
          })
      );
    });
  }

  /**
   * Získá výsledek funkce a obalí ho do Promise, kdyby funkce vrátila něco jiného.
   * To je obvyklejší přístup, než kontrolovat, jestli byla vrácena skutečně Promise.
   * @param {Function} cb Funkce, jejíž výsledek se má obalit.
   * @private
   * @returns {Promise.<*>} Výsledek funkce jako Promise.
   */
  wrapPromise(cb) {
    return new Promise((resolve) => resolve(cb()));
  }
}

export default StateMachine;
